import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert, Button } from '@mui/material';
import { Sync as SyncIcon } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import axiosInstance from '../../../../utils/axiosInstance';
import { IndicatorTypeLabels, SignalDirectionLabels, SignalDirectionColors, SignalStrengthLabels, AlertConditionLabels, AlertStatusLabels, AlertStatusColors } from '../../stockTradingEnums';

const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString() : '-';
const formatDateTime = (val) => val ? new Date(val).toLocaleString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);
const SignalChip = ({ signal }) => {
    const colorMap = { BUY: 'success', SELL: 'error', NEUTRAL: 'default', LONG_VOL: 'success', SHORT_VOL: 'error' };
    return <Chip label={signal || '-'} size="small" color={colorMap[signal] || 'default'} />;
};

// Period config: label, date format for X axis
const PERIODS = [
    { key: '1D', label: '1 day', dateFmt: { hour: '2-digit', minute: '2-digit' } },
    { key: '5D', label: '5 days', dateFmt: { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' } },
    { key: '1M', label: '1 month', dateFmt: { month: 'short', day: 'numeric' } },
    { key: '6M', label: '6 months', dateFmt: { month: 'short', day: 'numeric' } },
    { key: '1Y', label: '1 year', dateFmt: { month: 'short', day: 'numeric', year: '2-digit' } },
    { key: '5Y', label: '5 years', dateFmt: { month: 'short', year: '2-digit' } },
    { key: 'All', label: 'All time', dateFmt: { month: 'short', year: 'numeric' } },
];

const OverviewTab = ({ data, loading, error, symbolId }) => {
    const [chartPeriod, setChartPeriod] = useState('1D');
    const [chartData, setChartData] = useState({});       // keyed by period
    const [chartLoading, setChartLoading] = useState({});
    const [earningsData, setEarningsData] = useState(null);
    const [earningsFetching, setEarningsFetching] = useState(false);

    // Fetch chart data for a period from the dedicated endpoint
    const fetchChartData = useCallback(async (period) => {
        if (chartData[period] || chartLoading[period]) return;
        setChartLoading(prev => ({ ...prev, [period]: true }));
        try {
            const response = await axiosInstance.get(`/api/stock-trading/symbol-detail/${symbolId}/chart?period=${period}`);
            if (response.data.success) {
                setChartData(prev => ({ ...prev, [period]: response.data.data.prices || [] }));
            }
        } catch (err) {
            setChartData(prev => ({ ...prev, [period]: [] }));
        } finally {
            setChartLoading(prev => ({ ...prev, [period]: false }));
        }
    }, [symbolId, chartData, chartLoading]);

    // Fetch default period (1D) on mount, and whenever period changes
    useEffect(() => {
        if (symbolId) fetchChartData(chartPeriod);
    }, [chartPeriod, symbolId, fetchChartData]);

    // Sync earnings data from overview response
    useEffect(() => {
        if (data?.next_earnings) setEarningsData(data.next_earnings);
    }, [data]);

    // Fetch earnings from yfinance
    const handleFetchEarnings = async () => {
        setEarningsFetching(true);
        try {
            const response = await axiosInstance.post(`/api/market-data-ingestion/earnings-calendar/${symbolId}/fetch`);
            if (response.data.success && response.data.data) {
                setEarningsData(response.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch earnings:', err);
        } finally {
            setEarningsFetching(false);
        }
    };

    if (loading) return <TabLoading />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!data) return <TabLoading />;

    const sym = data.symbol || {};
    const indicators = data.indicators || [];
    const signals = data.signals || [];
    const alerts = data.alerts || [];
    const nextEarnings = earningsData || data.next_earnings;

    // Build chart display data from fetched prices
    const currentPeriodConfig = PERIODS.find(p => p.key === chartPeriod) || PERIODS[0];
    const rawPrices = chartData[chartPeriod] || [];
    const displayData = rawPrices.map(p => ({
        date: new Date(p.timestamp).toLocaleString('en-US', currentPeriodConfig.dateFmt),
        close: p.close_price,
    }));

    const prevClose = sym.previous_close;
    const isUp = displayData.length > 1 && displayData[displayData.length - 1].close >= displayData[0].close;
    const lineColor = isUp ? '#22c55e' : '#ef4444';

    // Calculate return for each period (from cached data)
    const getPeriodReturn = (prices) => {
        if (!prices || prices.length < 2) return null;
        const first = prices[0].close_price;
        const last = prices[prices.length - 1].close_price;
        if (!first) return null;
        return { pct: (last - first) / first * 100 };
    };

    return (
        <Box>
            {/* ===== Charts Section ===== */}
            <SectionTitle>Charts</SectionTitle>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                {chartLoading[chartPeriod] ? (
                    <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress size={32} /></Box>
                ) : displayData.length === 0 ? (
                    <EmptyState message="No price history available for this period" />
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={displayData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11, fill: '#94a3b8' }}
                                tickLine={false}
                                axisLine={{ stroke: '#e2e8f0' }}
                                interval="preserveStartEnd"
                            />
                            <YAxis
                                domain={['auto', 'auto']}
                                tick={{ fontSize: 11, fill: '#94a3b8' }}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(v) => `$${v.toFixed(0)}`}
                                width={60}
                            />
                            {prevClose && <ReferenceLine y={prevClose} stroke="#94a3b8" strokeDasharray="3 3" label={{ value: `Prev close ${prevClose.toFixed(2)}`, position: 'right', fontSize: 10, fill: '#94a3b8' }} />}
                            <RechartsTooltip
                                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                                formatter={(val) => [`$${Number(val).toFixed(2)}`, 'Close']}
                            />
                            <Line type="monotone" dataKey="close" stroke={lineColor} dot={false} strokeWidth={1.5} />
                        </LineChart>
                    </ResponsiveContainer>
                )}

                {/* Period Buttons */}
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    {PERIODS.map((pr) => {
                        const ret = getPeriodReturn(chartData[pr.key]);
                        return (
                            <Box
                                key={pr.key}
                                onClick={() => setChartPeriod(pr.key)}
                                sx={{
                                    flex: 1, minWidth: 80, textAlign: 'center', py: 1, px: 1,
                                    borderRadius: 1, cursor: 'pointer',
                                    bgcolor: chartPeriod === pr.key ? '#fff7ed' : 'transparent',
                                    border: chartPeriod === pr.key ? '1px solid #f59e0b' : '1px solid transparent',
                                    '&:hover': { bgcolor: '#fff7ed' },
                                }}
                            >
                                <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>
                                    {pr.label}
                                </Typography>
                                {ret?.pct != null ? (
                                    <Typography variant="caption" sx={{ color: ret.pct >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                                        {ret.pct >= 0 ? '+' : ''}{ret.pct.toFixed(2)}%
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        {chartLoading[pr.key] ? '...' : '-'}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Paper>

            {/* ===== Upcoming Earnings ===== */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Upcoming earnings</Typography>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={earningsFetching ? <CircularProgress size={14} /> : <SyncIcon />}
                    onClick={handleFetchEarnings}
                    disabled={earningsFetching}
                    sx={{ borderColor: '#f59e0b', color: '#f59e0b', '&:hover': { borderColor: '#d97706', bgcolor: '#fffbeb' } }}
                >
                    {earningsFetching ? 'Fetching...' : 'Fetch Earnings'}
                </Button>
            </Box>
            {nextEarnings ? (
                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>Next report date</Typography>
                            <Typography variant="h6" fontWeight={500}>
                                {nextEarnings.earnings_date ? `~ ${new Date(nextEarnings.earnings_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>Report period</Typography>
                            <Typography variant="h6" fontWeight={500}>
                                {nextEarnings.fiscal_quarter && nextEarnings.fiscal_year ? `${nextEarnings.fiscal_quarter} ${nextEarnings.fiscal_year}` : nextEarnings.fiscal_quarter || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>EPS estimate</Typography>
                            <Typography variant="h6" fontWeight={500}>
                                {nextEarnings.estimated_eps != null ? <>{nextEarnings.estimated_eps} <Typography component="span" variant="caption" color="text.secondary">USD</Typography></> : '\u2014'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>Revenue estimate</Typography>
                            <Typography variant="h6" fontWeight={500}>
                                {nextEarnings.estimated_revenue != null ? <>{(nextEarnings.estimated_revenue / 1e6).toFixed(2)} <Typography component="span" variant="caption" color="text.secondary">M USD</Typography></> : '\u2014'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            ) : (
                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                    <EmptyState message="No upcoming earnings scheduled" />
                </Paper>
            )}

            {/* ===== Key Stats ===== */}
            <SectionTitle>Key stats</SectionTitle>
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3}>
                    {[
                        { label: 'Market capitalization', value: sym.market_cap ? `${(sym.market_cap / 1e9).toFixed(2)} B` : '\u2014', unit: 'USD' },
                        { label: 'Previous Close', value: sym.previous_close ? sym.previous_close.toFixed(2) : '\u2014', unit: 'USD' },
                        { label: 'Day High', value: sym.day_high ? sym.day_high.toFixed(2) : '\u2014', unit: 'USD' },
                        { label: 'Day Low', value: sym.day_low ? sym.day_low.toFixed(2) : '\u2014', unit: 'USD' },
                        { label: 'Volume', value: sym.volume ? Number(sym.volume).toLocaleString() : '\u2014', unit: '' },
                        { label: 'Sector', value: sym.sector || '\u2014', unit: '' },
                        { label: 'Industry', value: sym.industry || '\u2014', unit: '' },
                        { label: 'Open', value: sym.day_open ? sym.day_open.toFixed(2) : '\u2014', unit: 'USD' },
                    ].map((item, i) => (
                        <Grid item xs={6} sm={3} key={i}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary">{item.label}</Typography>
                            <Typography variant="h6" fontWeight={500}>
                                {item.value} {item.unit && <Typography component="span" variant="caption" color="text.secondary">{item.unit}</Typography>}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Indicators */}
            <SectionTitle>Indicators</SectionTitle>
            {indicators.length === 0 ? <EmptyState message="No indicators available" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Indicator</TableCell>
                                <TableCell align="right">Latest Value</TableCell>
                                <TableCell>Signal</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {indicators.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{IndicatorTypeLabels[row.indicator_type] || row.indicator_type}</TableCell>
                                    <TableCell align="right">{formatNumber(row.latest_value)}</TableCell>
                                    <TableCell><SignalChip signal={row.latest_signal} /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Signals */}
            <SectionTitle>Signals</SectionTitle>
            {signals.length === 0 ? <EmptyState message="No signals available" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Direction</TableCell>
                                <TableCell>Strength</TableCell>
                                <TableCell align="right">Confidence</TableCell>
                                <TableCell>Generated At</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {signals.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Chip label={SignalDirectionLabels[row.direction] || row.direction} size="small" color={SignalDirectionColors[row.direction] || 'default'} />
                                    </TableCell>
                                    <TableCell>{SignalStrengthLabels[row.strength] || row.strength || '-'}</TableCell>
                                    <TableCell align="right">{formatPercent(row.confidence_score)}</TableCell>
                                    <TableCell>{formatDateTime(row.generated_at)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Alerts */}
            <SectionTitle>Price Alerts</SectionTitle>
            {alerts.length === 0 ? <EmptyState message="No alerts configured" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Condition</TableCell>
                                <TableCell align="right">Target Price</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {alerts.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{AlertConditionLabels[row.condition] || row.condition}</TableCell>
                                    <TableCell align="right">{formatPrice(row.target_price)}</TableCell>
                                    <TableCell>
                                        <Chip label={AlertStatusLabels[row.status] || row.status} size="small" color={AlertStatusColors[row.status] || 'default'} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default OverviewTab;
